import {MenuItem, SearchableMenu, SearchableMenuOptions, ShowDialog, ShowDropdown} from './DomMenu';
import {DIV, dragdrop1d, EL, hide, isVisible, renderIcon2, selectDomElement, show} from "./util/DomUtils";
import {BoundMethod, bufferToHex, debounced, ellipsize, shallowClone} from "./util/CoreUtils";
import {arraySignal, signal} from './lib/signal';
import { UiContext } from './util/UiContext';
import { DomWindow, DomWindowConfig } from './DomWindow';
import {Expression} from "./util/expr/Expr";
import {PropertyObject} from "./lib";

const GridCtrlStyles = `

:root {
	/************** LIGHT MODE *****************/
	--grid-selected-bg: #939;
	--grid-header-bg1: #ddd;
	--grid-header-bg2: #ccc;
	--grid-background: #eee;
	--grid-text-color: #000;
}

@media (prefers-color-scheme: dark) {
	:root {
		/************** DARK MODE *****************/
		--grid-selected-bg: #939;
		--grid-header-bg1: #555b;
		--grid-header-bg2: #444b;
		--grid-background: #3335;
		--grid-text-color: #ffff;
	}
}
.GridCtrl.display-card .tbody .tr { height: auto; background: white; padding: 8px; margin: 10px; box-shadow: 2px 2px 6px #888; min-width: initial !important; display: grid; }
.GridCtrl.display-card .treeChildren { margin-left: 10px; }
.GridCtrl.display-card .tbody .tr .td { position: initial; display: block; width: 100% !important; margin: 2px; }
.GridCtrl.display-card .treeRow>.tr>.td:first-child{ padding-left: initial!important; }

.GridCtrl{ font:8pt Helvetica,sans-serif; width: 100%; height:100%; display: flex; flex-direction: column; background: var(--grid-background); color: var(--grid-text-color); overflow:hidden; }
.GridCtrl *{  box-sizing: border-box; }
.GridCtrl .bodyouterDiv{ flex-grow: 1; overflow: auto }
.GridCtrl .bodyouterDiv .tbody{ position: relative; }
.GridCtrl .headouterDiv{ background:linear-gradient(to bottom, var(--grid-header-bg1) 0%, var(--grid-header-bg2) 100%);display: block; }
.GridCtrl .headouterDiv .thead{ position: relative;}
.GridCtrl .thead .tr { height: 25px; }
.GridCtrl .tbody .tr { height: 20px; }
.GridCtrl .th { padding: 7px 3px 3px; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border-right: 1px solid #aaa; position: absolute; }
.GridCtrl .td { padding: 2px 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border-right: 1px solid color-mix(in srgb, var(--grid-background), var(--grid-text-color) 10%); position: absolute;  height: 18px; }
.GridCtrl .tbody .tr.selected { background-color:var(--grid-selected-bg); }
.GridCtrl .tr.selected, .GridCtrl .tbody .tr.selected .td:first-child::before{ color: white!important;}
.GridCtrl .tbody .tr.selected:hover{ background-color:color-mix(in srgb, var(--grid-selected-bg), var(--grid-text-color) 5%); }
.GridCtrl .tbody .tr:hover{ background-color:color-mix(in srgb, var(--grid-background), var(--grid-text-color) 5%);}
.GridCtrl .tbody .tr:hover .td{ border-right-color:color-mix(in srgb, var(--grid-background), var(--grid-text-color) 10%);}
.GridCtrl .tbody .tr:hover+.treeChildren{ background-color:color-mix(in srgb, var(--grid-background), var(--grid-text-color) 3%);}
.GridCtrl .treeRow.expanded>.tr>.td:first-child::before{ content:"▼ ";width:15px;display: inline-block;color:#777; }
.GridCtrl .treeRow.collapsed>.tr>.td:first-child::before{ content:"► ";width:15px;display: inline-block;color:#777; }
.GridCtrl .treeRow.expanded>.tr>.td:first-child:hover::before,
.GridCtrl .treeRow.collapsed>.tr>.td:first-child:hover::before{ color:color-mix(in srgb, var(--grid-background), var(--grid-text-color) 80%); }
.GridCtrl .treeRow.empty>.tr>.td:first-child::before{ content:"▱ ";width:15px;display: inline-block;color:#777; }
.GridCtrl .td * { display: inline; padding: 0; margin: 0; white-space: nowrap; }
.GridCtrl .td br, .GridCtrl .td hr { display: none; }

.GridCtrl .td.value-error { font-style: italic; color: #b00; }
.GridCtrl .td.value-number, .GridCtrl .td.value-bytes { font-size: 90%; padding-top: 6px; }
.GridCtrl .td.value-number { font-family: serif; }
.GridCtrl .td.value-bytes { font-family: monospace; }
.GridCtrl .td.value-empty { color: #777; }
.GridCtrl .td.editor { padding-top: 0; }
.GridCtrl .selected .td.value-error { font-style: italic; color: rgb(248, 166, 166); }
.GridCtrl .selected .td.value-empty { color: #b9b9b9; }
.GridCtrl .selected .td a { color: white; }

.GridCtrl.display-card .tr.show-full-texts { height: auto !important; }
.GridCtrl .tr.show-full-texts .td,
.GridCtrl .tr .td.editor {height: auto; text-overflow: initial; overflow-y: visible; white-space: initial; word-break: break-all; overflow-x: hidden; }
.GridCtrl .tr.show-full-texts .td * { white-space: initial; }
`;


//import { EL, bufferToHex } from 'helper';
class ColInfo {
	key : string;
	expression : string = '';
	icon : string = '';
	title : string;
	width : number = 150;
	visible : boolean = false;
	available : boolean = true;
	sort : string = '';
	card : string = '';
	edit : string = 'auto';
	align : string = 'auto';
	private _expr? : Expression;
	private _iconExpr? : Expression;
	constructor(title:string,key:string,expression?:string){
		this.key=key; this.title=title; this.expression = expression || (`${key}`.match(/^[a-zA-Z_$][a-zA-Z0-9_$]*$/) ? `${key}` : '$this[' + JSON.stringify(key) + ']');
	}
	evaluate(o) {
		if (!this._expr || this._expr.exprString !== (this.expression || this.key))
			Object.defineProperty(this as any, '_expr', { enumerable: false, configurable: true, value: new Expression(this.expression || this.key) });
		return this._expr.evaluate(o);
	}
	evaluateIcon(o) {
		if (!this.icon) return null;
		if (!this._iconExpr || this._iconExpr.exprString !== (this.icon))
			Object.defineProperty(this as any, '_iconExpr', { enumerable: false, configurable: true, value: new Expression(this.icon) });
		return this._iconExpr.evaluate(o);
	}
	expressionIsValidLhs() {
		return this._expr.asReference !== null;
	}
	assign(o, val) {
		(this._expr.asReference.evaluate(o) as Function)(val);
	}
}
enum RowRenderResult {
	RENDERED = 0,
	NOT_IN_VIEWPORT = 1,
	NO_UPDATE = 2,
}
export enum ClickSelectMode {
	Disabled = '',
	Single = 'single',
	Multiple = 'multiple',
}
const GridCtrlDefaults : GridCtrlOptions = {
	initialCols: [],
	selectOnClick: ClickSelectMode.Disabled,
	allowMultiSelect: false,
	initialDisplay: {
		textMode: 'plain',
		textEllipsisLength: 1024,
		byteEllipsisLength: 200,
	},
	editable: false,
};
export type ObjectTreeCtrlOptions = GridCtrlOverrideOptions & {
	data?: object,
	expandRoot?: boolean,
	pathSkipLevels?: number,
};
export type GridCtrlOptions = GridCtrlOverrideOptions & {
	initialCols: ColInfoParam[],
}
export type GridCtrlOverrideOptions = {
	initialCols?: ColInfoParam[],
	settingsKey?: string;
	data?: any[];
	mobileDisplayStyle?: string,
	selectOnClick? : ClickSelectMode,
	allowMultiSelect? : boolean,
	initialDisplay?: GridDisplayState,
	editable?: boolean,
	itemClicked?: (obj: any, e: MouseEvent) => void;
	columnsChanged?: () => void;
	afterCellEdited?: (col : ColInfo, o : any, newValue : any) => void;
	rootObjectType?: string;
}
export type GridDisplayState = {
	displayStyle?: string,
	textMode? : string,
	textEllipsisLength? : number,
	byteEllipsisLength? : number,
	cols?: ColInfo[],
};
export type ColInfoParam = (string | { key: string, title?: string, expression?: string } | ColInfo);
export class GridCtrl {
	availableColSet = new Set<string>(); //Set stores insertion order :-)
	visibleCols : ColInfo[] = [];
	get cols () { return this.displayState.cols; }
	colsMap : {[key:string] : ColInfo} = {};
	data : any[] = [];
	//rowHeight = 20;
	//headerHeight = 26;
	public readonly rootElement : HTMLElement;
	table : HTMLDivElement;
	thead : HTMLDivElement;
	scrollContainer : HTMLDivElement;
	tbody : HTMLDivElement;
	tfooter : HTMLDivElement;
	tfooterCount : HTMLElement;
	tfooterLoadMore : HTMLAnchorElement;
	tfooterLoading: HTMLElement;
	itemClicked = signal<(obj: any, e: MouseEvent) => void>();
	columnsChanged = signal<() => void>();
	afterCellEdited = signal<(col : ColInfo, o : any, newValue : any) => void>();
	buildContextMenu = arraySignal<(targetItems : any[], o: any, cell: HTMLDivElement, e: MouseEvent) => any[]>();
	selectedItems = [];
	rowHeight;
	public readonly options : Readonly<GridCtrlOptions>;
	displayState : GridDisplayState;
	public static readonly _tr = Symbol('_tr');
	private trToData: WeakMap<HTMLDivElement, any> = new WeakMap();
	private _dataRef : any;

	constructor(root : HTMLElement | ShadowRoot, options: GridCtrlOptions)
	constructor(root : HTMLElement | ShadowRoot, visibleCols : ColInfoParam[]);
	constructor(root : HTMLElement | ShadowRoot, param: ColInfoParam[] | GridCtrlOptions) {
		if (param instanceof Array) {
			this.options = { initialCols: param };
		} else if (param && typeof param === 'object') {
			this.options = param;
		} else {
			throw "invalid options | visibleCols parameter to GridCtrl";
		}
		if (this.options.initialDisplay) Object.setPrototypeOf(this.options.initialDisplay, GridCtrlDefaults.initialDisplay);
		Object.setPrototypeOf(this.options, GridCtrlDefaults);
		this.displayState = shallowClone(this.options.initialDisplay);

		root.appendChild(this.rootElement = this.table = DIV({},
			//DIV({"class":"gridinnerDiv"},
				DIV({"class":"headouterDiv"}, 
					this.thead = DIV({"class":"thead", ondragover: this.handleHeaderDragOver, ondrop: this.handleHeaderDrop}),
				),
				this.scrollContainer = DIV({"class":"bodyouterDiv"}, 
					this.tbody = DIV({"class":"tbody"}),
					this.tfooter = DIV({"class":"tfooter",style:{color: '#555', padding:'20px',textAlign:'center'}},
						 this.tfooterCount = EL('b', {}, '0 entries shown '),
						 this.tfooterLoadMore = EL('a', { href:'javascript:', style: {display:'none'} }, 'load more ...'),
						 this.tfooterLoading = EL('div', { class:'loadindicator small inline', style: {display:'none'} }, EL('div',{}), EL('div',{}), EL('div',{})),
					),
				),
			//)
		));
		if (this.options.selectOnClick) {
			this.table.style.userSelect = 'none';
		}
		this.scrollContainer.addEventListener("scroll", debounced(40, async (e: MouseEvent)=> {
			this.thead.style.marginLeft = (-this.scrollContainer.scrollLeft) + "px";
			
			let viewportReached = false;
			//let from=Math.floor(this.scrollContainer.scrollTop / this.rowHeight),to=0
			for(let i = Math.floor(this.scrollContainer.scrollTop / this.rowHeight); i < this.data.length; i++) {
				const o = this.data[i];
				const result = await this.fillRowWithData(o[GridCtrl._tr], o, false);
				//to=i;
				if (result !== RowRenderResult.NOT_IN_VIEWPORT) viewportReached = true;
				else if (viewportReached) break; // we are past the viewport, no need to consider following rows
			}
			//console.log("rendered from ",from,"to",to)
		}, false), {passive: true});
		this.thead.addEventListener("contextmenu", (e)=> {
			this.showColMenu(e);
			e.preventDefault(); e.stopPropagation(); return false;
		})
		if (DomWindowConfig.isMobileMode && this.options.mobileDisplayStyle)
			this.displayState.displayStyle = this.options.mobileDisplayStyle;
		this.setCols(this.options.initialDisplay.cols || this.options.initialCols);
		if (this.options.settingsKey) {
			if (localStorage['GridCtrl:disp:'+this.options.settingsKey]) Object.assign(this.displayState, JSON.parse(localStorage['GridCtrl:disp:'+this.options.settingsKey]));
			else if (localStorage['GridCtrl:cols:'+this.options.settingsKey]) this.setCols(JSON.parse(localStorage['GridCtrl:cols:'+this.options.settingsKey]));
			this.setCols(this.displayState.cols);
			this.columnsChanged.connect(() => this.saveDisplayState());
		}
		if (this.options.data) this.setData(this.options.data);

		if (this.options.itemClicked) this.itemClicked.connect(this.options.itemClicked);
		if (this.options.columnsChanged) this.columnsChanged.connect(this.options.columnsChanged);
		if (this.options.afterCellEdited) this.afterCellEdited.connect(this.options.afterCellEdited);
	}
	saveDisplayState() {
		if (this.options.settingsKey)
			localStorage['GridCtrl:disp:'+this.options.settingsKey] = JSON.stringify(this.displayState);
	}
	async editColumn(colKey) {
		const response = (await ShowDialog("Edit column expression", (x) => {
			let expr : Expression;
			try {
				expr = new Expression(x);
			} catch(e) {
				return [{icon:'⚠️',text:`Parse Error: ${e}`,disabled:true}];
			}
			return [{text:`${expr.toString()}`,expr:x}].concat(this.data.slice(0,20).map(d => {
				try {
					return {text: ""+expr.evaluate(d), expr:x};
				} catch(e) {
					return {icon:'⚠️',text:`Eval Error: ${e}`,disabled:true,expr:undefined}
				}
			}));
		}, {initialSearch: this.colsMap[colKey].expression || this.colsMap[colKey].key, width:650})).expr;
		if (!response) return;
		this.colsMap[colKey].expression = response;
		this.updateCols();
	}
	showColMenu(e: MouseEvent | null) {
		const colHeader = (e?.target as HTMLElement)?.closest('div.th[data-col-key]') as HTMLElement;
		const colKey = colHeader?.getAttribute('data-col-key');
		const colIdx = this.cols.indexOf(this.colsMap[colKey]);
		const pre = colHeader ? this._buildColOptionsContextMenu(colHeader, this.colsMap[colKey], colIdx, e) : [];
		const post = this._buildGridOptionsContextMenu(colHeader, this.colsMap[colKey], colIdx, e);

		ShowDropdown(e, (search) => {
			const filter = SearchableMenu.menuFilter(search);
			const cm : any[] = [];
			let insertPos = colKey ? (colIdx + ((e as any)?.layerX >= (e?.target as HTMLElement)?.clientWidth/2 ? 1 : 0)) : this.cols.length;
			if (search.startsWith('[{') && search.endsWith('}]')) {
				const data = JSON.parse(search);
				if (data instanceof Array && data.every(x => 'key' in x && 'title' in x && 'expression' in x)) {
					return [{icon:'📋',text:"Apply Header",action:()=>{ this.setCols(data); }}];
				}
			}
			if (search.startsWith('+')) {

			}
			if (pre.length && !search) {
				cm.push(...pre);
				cm.push({text:'-'});
			}
			this.cols.filter((c) => (c.visible || c.available) && filter(c.title)).forEach((c) => cm.push({text:c.title,action:() => this.toggleCol(c, insertPos++),icon:c.visible?'check_box.png':'check_box_uncheck.png'}))
			if (!search) cm.push({text:"-"});
			this.availableColSet.forEach((c) => c in this.colsMap || filter(c) && cm.push({text:c, action: () => this.addCols([c], insertPos++)}));

			if (search && search.indexOf('.') > 0) {
				try {
					let o = this.data[0];
					let path = search.substring(0, search.lastIndexOf('.'));
					let lastComponent = search.substring(search.lastIndexOf('.') + 1).toLowerCase();
					var result = GridCtrl.walkObjectPath(o, path);
					if (result && typeof result === 'object')
						for (const key in result)
							if (key.toLowerCase().indexOf(lastComponent) !== -1)
								cm.push({ icon: 'plus_button.png', text: path + '.' + key, action: () => this.addCols([{ key: path + '.' + key, title: path + '.' + key, expression: path + '.' + key }], insertPos++) });
				}catch{}
			}
			cm.push({ icon: 'plus_button.png', text: search, action: () => this.addCols([{ key: search, title: search, expression: search }], insertPos++) });

			if (!search) {
				cm.push({text:'-'});
				cm.push(...post);
			}
			return cm;
		}, { placeholder: 'Add column ...' });
	}
	_buildColOptionsContextMenu(colHeader: HTMLElement, colInfo: ColInfo, colIdx: number, e: MouseEvent | null) {
		console.log("_buildColOptionsContextMenu", colHeader, colInfo, colIdx, e)
		const cm = [];
		if (colHeader) cm.push({text:"Edit Column Expression", icon: '📝', action:() => this.editColumn(colInfo.key)});
		if (colHeader) cm.push({text:"Delete Column", icon: '❌', action:() =>{ this.cols.splice(colIdx, 1); this.updateCols(); }});
		return cm;
	}
	_buildGridOptionsContextMenu(colHeader: HTMLElement | undefined, colInfo: ColInfo | undefined, colIdx: number | undefined, e: MouseEvent | null) {
		const cm = [];
		cm.push({text:"Card Mode", icon: this.displayState.displayStyle === 'card' ? 'check_box.png' : 'check_box_uncheck.png', action:() => { this.displayState.displayStyle = this.displayState.displayStyle === 'card' ? '' : 'card'; this.updateCols(); } });
		cm.push({text:"Enable Markdown", icon: this.displayState.textMode === 'md' ? 'check_box.png' : 'check_box_uncheck.png', action:() => { this.displayState.textMode = this.displayState.textMode === 'md' ? 'plain' : 'md'; this.updateCols(); } });
		cm.push({text:"Options", icon: '⚙️', action:() => ObjectTreeCtrl.showDialog(this.displayState, 'Options', { editable: true, afterCellEdited: () => this.updateCols() })});
		cm.push({text:"Copy Header", icon: '📄', action:()=>{const info = JSON.stringify(this.cols); navigator.clipboard.writeText(info); }});
		cm.push({text:"Reset Header", icon: '↩️', action:()=>{ this.setCols(this.options.initialCols); this.updateCols();}});
		return cm;
	}
	sortBy(colKey: string, ascending: boolean) {
		const col = this.colsMap[colKey];
		function quietEval(o) {
			try {
				return col.evaluate(o);
			} catch {
				return undefined;
			}
		}
		function compare(a, b) {
			if (a === b) return 0;
			if (typeof a === 'undefined') return -1;
			if (typeof b === 'undefined') return 1;
			if (a instanceof Date) a = +a;
			if (b instanceof Date) b = +b;
			if (typeof a === 'number' && typeof b === 'number') return a - b;
			return `${a}`.localeCompare(`${b}`, 'de', {numeric: true});
		}
		this.data.sort((a, b) => compare(quietEval(a), quietEval(b)) * (ascending ? 1 : -1));
		this.setData(this.data, false);
	}
	async headerClick(colKey: string, event?: MouseEvent) {
		await new Promise(r => setTimeout(r, 1));
		const col = this.colsMap[colKey];
		function quietEval(o) {
			try {
				return col.evaluate(o);
			} catch {
				return undefined;
			}
		}
		ShowDropdown(event, (search) => {
			if (search) {
				return this.data.mapDefined(o => {
					const v = `${quietEval(o)}`;
					if (v.includes(search)) return { text: v, action: () => { this.addToSelection([o]); this.scrollIntoView(o); } };
				});
			}
			return [{
				text: "Sort ascending", action: () => {
					col.sort = 'asc';
					this.sortBy(colKey, true);
				}, icon: "sort_ascending.png",
			}, {
				text: "Sort descending", action: () => {
					col.sort = 'desc';
					this.sortBy(colKey, false);
				}, icon: "sort_descending.png",
			}]
		}, { placeholder: 'Search in column contents ...'});
	}
	fillHeaderRow(tr : HTMLDivElement) : HTMLDivElement {
		for (var i = 0; i<this.visibleCols.length; i++) {
			const col = this.visibleCols[i];
			const el = <HTMLDivElement> EL("div", {"class":"th","data-col-key":col.key}, col.title);
			tr.appendChild(el);
			dragdrop1d(el, 'x', [{
				button: 1, condition: (offset, size) => offset > size - 15,
				init: (pos) => col.width,
				transform: (newpos) => el.style.width = newpos + 'px',
				finishMove: (newpos) => {
					col.width = Math.max(5, newpos);
					this.applyCellStyles();
					this.columnsChanged();
				},
				doubletap: () => {
					const space = this.table.clientWidth - this.visibleCols.reduce((p,c) => p+c.width, 10) + col.width;
					if (space > 5) col.width = space;
					this.applyCellStyles();
					this.columnsChanged();
				}
			}, {
				button: 1, condition: (offset, size) => offset < size - 15,
				init: (pos) => {
					el.style.zIndex="9999"; el.style.opacity="0.7";
					return parseInt(el.style.left)
				},
				transform: (newpos, diff, pointerpos) => {
					el.style.left = newpos + "px";
				},
				finishMove: (newpos, diff, pointerpos) => {
					let x = 0, index = this.cols.length;
					const elements = tr.children;
					for(let i = 0; i < elements.length; i++) {
						if (elements[i] === el) continue;
						const bb = elements[i].getBoundingClientRect();
						if (pointerpos < bb.x + bb.width / 2) {
							index = this.cols.indexOf(this.colsMap[elements[i].getAttribute('data-col-key')]);
							break;
						}
					}
					this.moveColTo(col.key, index);
					this.updateCols();
				},
				abortMove: () => { delete el.style.zIndex; delete el.style.opacity; },
				tap: event => {
					this.headerClick(col.key, event);
				}
			}]);
		}
		this.applyCellStylesOnRow(tr);
		return tr;
	}
	@BoundMethod()
	handleHeaderDragOver(e: DragEvent) {
		if (e.dataTransfer.types.includes('text/vnd.pinboard-json-path+plain')) {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'link';
		}
	}
	@BoundMethod()
	handleHeaderDrop(e: DragEvent) {
		if (e.dataTransfer.types.includes('text/vnd.pinboard-json-path+plain')) {
			e.dataTransfer.dropEffect = 'link';
			const key = e.dataTransfer.getData('text/vnd.pinboard-json-path+plain');
			const th = (e.target as HTMLElement).closest('[data-col-key]');

			const index = th ? this.cols.indexOf(this.colsMap[th.getAttribute('data-col-key')]) : this.cols.length;
			this.addCols([{key, title: key, expression: key}], index);
		}
	}
	addCols(cols: ColInfoParam[], index?: number){
		for(let col of cols) {
			if (typeof col === 'string') {
				col = { key: col };
			}
			if (!col.title) col.title = col.key;
			if (!col.key || typeof col.key !== 'string') throw "invalid col key "+col.key+" provided to addCols";
			if (col.key in this.colsMap) {
				this.colsMap[col.key].visible=true;
				if (typeof index === 'number') this.moveColTo(col.key, index);
			} else {
				const info = this.colsMap[col.key] = Object.assign(new ColInfo(col.title, col.key), {visible:true}, col);
				if (typeof index === 'number')
					this.cols.splice(index, 0, info);
				else
				    this.cols.push(info);
			}
		}
		this.updateCols();
	}
	moveColTo(key: string, index: number) {
		const curIndex = this.cols.indexOf(this.colsMap[key]);
		this.cols.splice(curIndex, 1);
		this.cols.splice(index - (curIndex < index ? 1 : 0), 0, this.colsMap[key]);
	}
	addCol(title:string,key:string){
		this.addCols([{ title, key }])
	}
	setCols(cols : ColInfoParam[]) {
		this.displayState.cols = []; this.colsMap = {};
		this.addCols(cols);
	}

	updateCols() {
		this.visibleCols = this.cols.filter((c) => c.visible);
		this.colsMap = {};
		this.cols.forEach(col => this.colsMap[col.key] = col);
		this.thead.innerHTML = "";
		this.thead.appendChild(this.fillHeaderRow(EL("div",{"class":"tr"}) as HTMLDivElement));
		for(var i =0; i < this.data.length; i++) {
			this.fillRowWithData(this.data[i][GridCtrl._tr], this.data[i], true);
		}
		this.table.className = 'GridCtrl display-'+this.displayState.displayStyle;
		this.columnsChanged();
	}
	toggleCol(col:ColInfo, index?: number) {
		col.visible = !col.visible;
		if (col.visible && typeof index === 'number') this.moveColTo(col.key, index);
		this.updateCols();
	}
	getColStyle(index:number) {
		return this.visibleCols[index];
	}
	applyCellStyle(columnIndex:number, cell:HTMLDivElement) : number {
		var style = this.getColStyle(columnIndex);
		cell.style.width = style.width + "px";
		if (this.displayState.displayStyle === 'card' && style.card) {
			const s = style.card.split(' ');
			['grid-column-start', 'grid-column-end', 'grid-row-start', 'grid-row-end'].forEach((x,i) => cell.style[x] = s[i]);
		}
		if (style.align && style.align !== 'auto') cell.style.textAlign = style.align;
		return style.width;
	}
	applyCellStylesOnRow(tr : HTMLDivElement) {
		if (!tr.children.length) return;
		var x = 0;
		for(var j = 0; j < this.visibleCols.length; j++) {
			const node = tr.children[j] as HTMLDivElement;
			node.style.left = x + "px";
			x += this.applyCellStyle(j, node);
		}
		tr.style.minWidth = x + "px";
	}
	applyCellStyles() {
		this.applyCellStylesOnRow(this.thead.children[0] as HTMLDivElement);
		for(var i =0; i < this.data.length; i++) {
			this.applyCellStylesOnRow(this.data[i][GridCtrl._tr]);
		}
	}

	clearRows(keepLoadMore = false) {
		this.availableColSet = new Set();
		this.tbody.innerHTML = "";
		this.data = [];
		if (!keepLoadMore) {
			this.tfooterLoadMore.style.display = 'none';
			this.tfooterLoadMore.onclick = null;
		}
		this.updateCount();
	}
	appendRow(o : any, container : HTMLDivElement = this.tbody) {
		this.appendRows([o], container);
	}
	async appendRows(objects : any[], container : HTMLDivElement = this.tbody) {
		let startIndex = this.data.length;
		this.data.push(...objects);
		await this._createTableRows(objects, container, startIndex);
		this.updateCount();
	}
	async _createTableRows(objects : any[], container : HTMLDivElement, startIndex) {
		const trs = objects.map(o => {
			var keys = Object.keys(o);
			for (var i=0;i<keys.length;i++) if(!keys[i].startsWith("_")) this.availableColSet.add(keys[i]);

			var row = DIV({"class":"tr"});
			Object.defineProperty(o, GridCtrl._tr, { enumerable: false, configurable: true, value: row });
			this.trToData.set(row, o);
			row.addEventListener("click", (e) => this._onRowClick(o, e, (e.target as HTMLElement).closest('div.td') as HTMLDivElement));
			row.addEventListener("contextmenu", this._onRowContextMenu.bind(this, o))
			return row;
		});
		container.append(...trs);
		let viewportReached = false;
		if (trs[0]?.clientHeight) this.rowHeight = trs[0].clientHeight;
		for(let i = Math.max(0, Math.floor(this.scrollContainer.scrollTop / this.rowHeight) - startIndex); i < trs.length; i++) {
			const result = await this.fillRowWithData(trs[i], objects[i], false);
			if (result !== RowRenderResult.NOT_IN_VIEWPORT) viewportReached = true;
			else if (viewportReached) break; // we are past the viewport, no need to consider following rows
		}
	}
	addToSelection(items : any[], toggle = false) {
		for (const o of items) {
			const pos = this.selectedItems.indexOf(o);
			if (pos !== -1) {
				this.selectedItems.splice(pos, 1);
				if (toggle) {
					o[GridCtrl._tr].classList.remove('selected');
					continue;
				}
			}
			o[GridCtrl._tr].classList.add('selected');
			this.selectedItems.push(o);
		}
	}
	scrollIntoView(o) {
		o[GridCtrl._tr].scrollIntoView();
	}
	deselectAll() {
		for(const item of this.selectedItems) {
			item[GridCtrl._tr].classList.remove('selected');
		}
		this.selectedItems = [ ];
	}
	_onRowClick(o : any, e: MouseEvent, cell: HTMLDivElement | null) {
		if (cell) {
			if (cell.classList.contains('editor')) return;
			if (this.startCellEdit(o, cell)) return;

			// de-ellipsize
			if (cell.parentElement.classList.contains('show-full-texts')) {
				cell.parentElement.classList.remove('show-full-texts');
				cell.parentElement.style.height = null;
			} else {
				if (cell.offsetWidth < cell.scrollWidth) {
					cell.parentElement.classList.add('show-full-texts');
					cell.parentElement.style.height = Math.max(...[...cell.parentElement.children as any].map(x => x.scrollHeight)) + 'px';
				}
			}
		}
		if (this.options.selectOnClick) {
			if (this.options.allowMultiSelect && (this.options.selectOnClick === ClickSelectMode.Multiple || e?.ctrlKey)) {
				this.addToSelection([o], true);
			} else if (this.options.allowMultiSelect && (e?.shiftKey)) {
				e.preventDefault();
				this._rangeSelectTo(o);
			} else {
				this.deselectAll();
				this.addToSelection([o]);
			}
			e?.preventDefault();
		}
		this.itemClicked.emit(o, e);
	}
	_rangeSelectTo(o : any) {
		const last = this.selectedItems[this.selectedItems.length - 1];
		const startIdx = this.data.indexOf(last);
		const endIdx = this.data.indexOf(o);
		this.addToSelection(this.data.slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1));
	}
	mapObjectToItem(o : any): { props: PropertyObject } & object {
		return { props: o };
	}
	async _buildContextMenu(targetItems : any[], o: any, cell: HTMLDivElement, e: MouseEvent): Promise<MenuItem[]> {
		const defaultActions = [
			{
				text: 'Copy Cell Value', action: cell ? () => navigator.clipboard.writeText(`${this.getCellData(o, this.colsMap[cell.getAttribute('data-col-key')])}`) : undefined,
			},
			{
				text: targetItems === this.selectedItems ? 'Copy Selection as JSON' : 'Copy Row as JSON', action: () => navigator.clipboard.writeText(JSON.stringify(targetItems.map(x => x.props))),
			},
			{
				text: 'View Row as Tree', action: () => ObjectTreeCtrl.showDialog(o),
			},
		];
		if (UiContext.instance.sel) {
			const selActions = await UiContext.instance.sel.buildContextMenu('gridctx', targetItems, e, this.options?.rootObjectType);
			if (selActions.length)
				return selActions.concat([{text: '-'}], defaultActions);
		}
		return defaultActions;
	}
	async _onRowContextMenu(o : any, e: MouseEvent) {
		const cell = (e.target as HTMLElement).closest('div.td') as HTMLDivElement;
		const targetItems = (this.selectedItems.indexOf(o) === -1 ? [o] : this.selectedItems).map(this.mapObjectToItem, this);
		if (DomWindowConfig.isMobileMode) {
			if (this.selectedItems.indexOf(o) === -1 && this.options.allowMultiSelect) {
				this._rangeSelectTo(o);
			}
		}
		e.preventDefault();
		const actions = await this._buildContextMenu(targetItems, o, cell, e);
		actions.push(...this.buildContextMenu.flatCollect(targetItems, o, cell, e));
		actions.sort((a, b) => ((a.pos||9999) - (b.pos||9999)));
		ShowDropdown(e, actions);
        return false;
	}
	getCellData(o: any, col: ColInfo) {
		if (col.key === '$INDEX$') return this.data.indexOf(o)
		return col.evaluate(o);
	}
	async fillRowWithData(tr : HTMLDivElement, o : any, forceRerender: boolean): Promise<RowRenderResult> {
		//console.log(tr.offsetTop ,this.scrollContainer.scrollTop , this.scrollContainer.offsetHeight)
		const thisPos = tr.offsetTop, scrollPos = this.scrollContainer.scrollTop, containerHeight = this.scrollContainer.offsetHeight || 1000;
		if (thisPos < scrollPos - containerHeight || thisPos > scrollPos + 2 * containerHeight) {
			if (forceRerender) tr.replaceChildren();
			return RowRenderResult.NOT_IN_VIEWPORT;
		}
		if (tr.children.length > 0 && !forceRerender) return RowRenderResult.NO_UPDATE;
		const cells = this.visibleCols.map(col => DIV({"class":"td","data-col-key":col.key}));
		tr.replaceChildren(...cells);
		this.applyCellStylesOnRow(tr);
		for (var i = 0; i<this.visibleCols.length; i++) {
			const col = this.visibleCols[i];
			const cell = cells[i];
			this.formatValue(o, col, cell);  // do not await to allow async rendering of values
			const self = this;
			cell.addEventListener('dblclick', function(e) {
				if (this.parentElement.classList.contains('show-full-texts') || this.offsetWidth < this.scrollWidth) {
					this.parentElement.classList.add('show-full-texts');
					this.parentElement.style.height = Math.max(...[...this.parentElement.children as any].map(x => x.scrollHeight)) + 'px';
					e.preventDefault(); e.stopPropagation(); selectDomElement(this);
				}

                UiContext.instance.sel?.handleHotkeyByName?.('DblClick', null, self.selectedItems.map(self.mapObjectToItem, self), new Set(['gridctx']));
			})
		}
		return RowRenderResult.RENDERED;
	}
	async formatValue(o : any, col : ColInfo, into: HTMLDivElement) {
		try {
			let value = this.getCellData(o, col);
			value = await Promise.resolve(value);
			if (typeof value == "undefined") {
				into.classList.add('value-empty');
				into.innerText = "(undef.)";
			} else if (value === null) {
				into.classList.add('value-empty');
				into.innerText = "(null)";
			} else if (typeof value == "number") {
				into.classList.add('value-number');
				into.innerText = ""+value;
				if (col.align === 'auto') into.style.textAlign = 'right';
			} else if (typeof value == "boolean") {
				into.classList.add('value-boolean');
				into.innerHTML = `<input type="checkbox" ${this.options.editable?'':'disabled'} ${value?'checked':''}>`;
			} else if (value instanceof Date) {
				into.classList.add('value-date');
				into.title = value.toLocaleString('sv-SV');
				if (value.isToday())
					into.innerText = value.toLocaleTimeString('sv-SV', {hour:'2-digit',minute:'2-digit'});
				else
					into.innerText = value.toLocaleString('sv-SV', {day: '2-digit', month: '2-digit', year: 'numeric', hour:'2-digit',minute:'2-digit'});
			} else if (value instanceof Uint8Array || value instanceof ArrayBuffer) {
				into.classList.add('value-bytes');
				into.title = into.innerText = bufferToHex(value, " ", this.displayState.byteEllipsisLength) + (value.byteLength > this.displayState.byteEllipsisLength ? ` [${value.byteLength-this.displayState.byteEllipsisLength} bytes omitted...]` : '');
			} else if (value instanceof Array || typeof value === 'object') {
				into.title = into.innerText = this.prepareText(JSON.stringify(value));
			} else if (typeof value === 'string' && this.displayState.textMode === 'md') {
				into.innerHTML = UiContext.instance.markdown.render(this.prepareText(value), true);
				into.title = value;
				into.classList.add('value-string');
			} else {
				into.title = into.innerText = this.prepareText(""+value);
				if (typeof value === 'string') into.classList.add('value-string');
			}
		} catch(ex) {
			into.innerText = 'ERR: '+ex;
			into.classList.add('value-error');
		}
		try {
			if (col.icon) renderIcon2(`${col.evaluateIcon(o)}`, 16, UiContext.instance.db, EL('span', {prependTo: into}));
		}catch{}
	}
	startCellEdit(o : any, cell : HTMLDivElement) {
		if (!this.options.editable) return false;
		const col = this.colsMap[cell.getAttribute('data-col-key')];
		if (!col.expressionIsValidLhs()) return false;
		if (cell.classList.contains('editor')) return false;
		
		let v : () => any;
		const exit = () => {
			this.fillRowWithData(cell.parentElement as HTMLDivElement, o, true);
		}
		const confirm = () => {
			this.afterCellEdit(col, o, v());
			exit();
		}
		const onkeydown = (e) => {
			if (e.key === 'Escape') { exit(); e.preventDefault(); e.stopPropagation(); }
			if (e.key === 'Enter') { confirm(); e.preventDefault(); e.stopPropagation(); }
		}
		const oncontextmenu = (e) => e.stopPropagation();

		let cellData = null;
		try {
			cellData = this.getCellData(o, col);
		} catch {}
		if ((col.edit === 'auto' || col.edit === 'boolean') && cell.classList.contains('value-boolean')) {
			this.afterCellEdit(col, o, cell.querySelector('input').checked);
			return true;
		} else if (((col.edit === 'auto' && cell.classList.contains('value-number')) || col.edit === 'number')) {
			let input = EL('input', {type:'number',value:cellData,style:'width:100%',onkeydown,oncontextmenu});
			v = () => parseFloat(input.value);
			cell.replaceChildren(input);
			input.focus({ preventScroll: true }); input.select();
			cell.classList.add('editor');
			return true;
		} else if (((col.edit === 'auto' && cell.classList.contains('value-string')) || col.edit === 'string')) {
			let input = EL('input', {type:'text',value:cellData,style:'width:100%',onkeydown,oncontextmenu});
			v = () => input.value;
			cell.replaceChildren(input);
			input.focus({ preventScroll: true }); input.select();
			cell.classList.add('editor');
			return true;
		}
		return false;
	}
	afterCellEdit(col : ColInfo, o : any, newValue : any) {
		col.assign(o, newValue);
		this.afterCellEdited.emit(col, o, newValue);
	}

	prepareText(value: string): string {
		return ellipsize(value.replace(/\n|\r/g, " "), this.displayState.textEllipsisLength, ' [%d omitted...]');
	}
	async setData(data : any[] | AsyncIterator<any[], any[]>, scrollToTop? : boolean) {
		//console.log("grid setData",data)
		this.clearRows();
		this._dataRef = data;
		if ('next' in data) {
			const loadNext = async () => {
				try {
					this.tfooterLoadMore.style.display = 'none';
					show(this.tfooterLoading);
					const iterResult = await data.next();
					if (this._dataRef !== data) return; // race condition
					await this.appendRows(iterResult.value);
					if (this.tfooterLoadMore.onclick == loadNext)
						this.tfooterLoadMore.style.display = iterResult.done ? 'none' : '';
				} catch(ex) {
					this.tfooterLoadMore.style.display = 'none';
					this.tfooterCount.innerText = 'ERR: ' + ex.toString();
					throw ex;
				} finally {
					hide(this.tfooterLoading);
				}
			};
			this.tfooterLoadMore.onclick = loadNext;
			await loadNext();
		} else if ('forEach' in data) {
			this.tfooterLoadMore.style.display = 'none';
			await this.appendRows(data);
		} else {
			throw "invalid data parameter to GridCtrl.setData"
		}
	}
	updateCount() {
		this.tfooterCount.innerText = ''+this.data.length+' entries shown ';
	}
	resizeCanvas() {}
	dataFromElement(el: HTMLElement) : any {
		const tr = el.closest("div.tr") as HTMLDivElement;
		return this.trToData.get(tr);
	}


	static walkObjectPath(o: any, pathString: string) {
		return new Expression(pathString).evaluate(o);
	}
	
}


export type TreeNode = [ {[key: string]: any}, TreeNode[] ];
export type PromisedTreeNode = [ PromisedGridValue, PromisedTreeNode[] | Promise<PromisedTreeNode[]> ];
export type PromisedGridValue = {[key: string]: any} | Promise<{[key: string]: any}>;

/**
 * A GridCtrl with support for recursive tree structures, in which nodes can be expanded/collapsed by the user.
 *
 * The tree structure is represented by `TreeNode` types. A `TreeNode` is a two-element array (`[hash, children]`),
 * whose first element is an object representing the properties of the node itself (the *hash* in `TreeCtrl` terms, or
 * the `o` in GridCtrl terms), and whose second element is an array of child `TreeNode`s (the *children*).
 */
export class TreeCtrl extends GridCtrl {
	public static readonly _level = Symbol('level');

	expandStates : any = {};

	async setTreeData(treeData : PromisedTreeNode, defaultExpanded : number = 10) {
		this.clearRows();
		await this.addRoot(treeData, defaultExpanded);
	}
	async addRoot(treeData : PromisedTreeNode, defaultExpanded : number = 10) {
		await this.appendRows([treeData], undefined, defaultExpanded);
		this.updateCount();
	}
	async appendRows(objects: PromisedTreeNode[], parentObject : any = undefined, defaultExpanded = 0): Promise<void> {
		let parentContainer = this.tbody, level = 0, dataIdx = this.data.length;
		if (parentObject) {
			dataIdx = this.data.indexOf(parentObject) + 1;
			if (dataIdx > 0) {
				parentContainer = parentObject[TreeCtrl._tr].nextSibling;
				level = parentObject[TreeCtrl._level] + 1;
				while (dataIdx < this.data.length && this.data[dataIdx]?.[TreeCtrl._level] >= level) dataIdx++;  // find end of container
			} else throw "appendRows called with invalid parentObject"
		}
		await this.recursiveAdd(objects, parentContainer, level, defaultExpanded, undefined, dataIdx);
		this.updateCount();
	}
	async headerClick(colKey: string, event?: MouseEvent) {
	}
	async fillRowWithData(tr : HTMLDivElement, o : any, forceRerender: boolean): Promise<RowRenderResult> {
		const result = await super.fillRowWithData(tr, o, forceRerender)
		if (result === RowRenderResult.RENDERED) {
			var firstCol = tr.children[0] as HTMLElement;
			firstCol.style.paddingLeft=o[TreeCtrl._level]*15 + "px";
		}
		return result;
	}
	_onRowClick(o: any, e: MouseEvent, cell: HTMLDivElement | null) {
		if (cell && cell.parentElement.firstChild === cell) {
			this.toggleExpand(o);
			if (e.offsetX > o[TreeCtrl._level]*15 && e.offsetX < o[TreeCtrl._level]*15+15) return;
		}
		super._onRowClick(o, e, cell);
	}

	onBeforeExpand(treeRow : HTMLDivElement, o : any) {
	}

	onPrepareData(hash:any) {
	}

	toggleExpand(o : any, forceState?: boolean) {
		const tableRow: HTMLDivElement = o[GridCtrl._tr];
		const parent = tableRow.parentNode as HTMLDivElement;
		const childContainer = tableRow.nextElementSibling as HTMLElement;
		const newState = !isVisible(childContainer);
		if (typeof forceState === 'boolean' && forceState !== newState) return;
		if (newState) this.onBeforeExpand(parent, o);
		show(childContainer, newState);
		if ('_stateId' in o) this.expandStates [o['_stateId']] = newState;
		parent.classList.remove("collapsed");parent.classList.remove("expanded");
		parent.classList.add(newState?"expanded":"collapsed");
	}

	async resolveTreeNode(data : PromisedTreeNode): Promise<[any, PromisedTreeNode[]]> {
		const [hash_, children_] = await Promise.resolve(data);
		const hash = await Promise.resolve(hash_);
		const children = await Promise.resolve(children_);
		return [hash, children];
	}

	async recursiveAdd(objects : PromisedTreeNode[], parentContainer : HTMLDivElement, level : number, defaultExpanded : number, schemaRef?: string, dataIdx: number = -1): Promise<number> {
		if (!dataIdx) dataIdx = this.data.length;
		for (const packetData of objects) {
			var rowContainer = DIV({});
			parentContainer.appendChild(rowContainer);
			const [hash, children] = await this.resolveTreeNode(packetData);
			Object.defineProperty(hash, TreeCtrl._level, { enumerable: false, configurable: true, value: level });
			this.onPrepareData(hash);

			var state = "empty", expanded = defaultExpanded > 0;
			if (children.length > 0 || hash['_lazyLoadChildren']) {
				state = "has-children ";
				if ('_stateId' in hash && typeof this.expandStates[hash['_stateId']] === "boolean") expanded=this.expandStates[hash['_stateId']];
				state += expanded?"expanded":"collapsed";
			}
			rowContainer.className = "treeRow "+state;
			this.data.splice(dataIdx, 0, hash);
			await this._createTableRows([hash], rowContainer, dataIdx++);
			
			var childContainer = DIV({"class":"treeChildren"});
			rowContainer.appendChild(childContainer);
			if (!expanded) hide(childContainer);
			else this.onBeforeExpand(rowContainer, hash);

			dataIdx = await this.recursiveAdd(children, childContainer, level+1, defaultExpanded > 0 ? defaultExpanded - 1 : 0, schemaRef, dataIdx);
			if (level === 0 && !rowContainer.isConnected) return;
		}
		return dataIdx;
	}
}

/**
 * A TreeCtrl that automatically renders arbitrary JavaScript objects as a tree structure, with the object
 * properties as nodes.
 *
 * Internally, the rendered object is converted lazily (when the user expands a node) into the `TreeNode` structure
 * as understood by the underlying `TreeCtrl`, where the *hash* of each node contain `name`, `type` and
 * `value` properties. `name` is the property name (or array index) with which this node is stored in its parent,
 * `type` is a string representing the value type, and `value` is set to the primitive value of this node, or the empty
 * string if this node is an object or array whose value is represented using TreeCtrl *children*.
 */
export class ObjectTreeCtrl extends TreeCtrl {
	public static readonly kvObjectRef = Symbol('kvObjectRef');
	public static readonly objectToTreeValueTypes = [Uint8Array, ArrayBuffer];
	private _object : object;

	constructor(root : HTMLElement | ShadowRoot, options: Readonly<ObjectTreeCtrlOptions>) {
		super(root, Object.assign(
			{ initialCols: [{key:'name',edit:'',align:'left'},{key:'value',width:300,align:'left'}], settingsKey: 'objectTree' },
			options,
			{ data: null })
		);
		if (options.data) this.setObject(options.data);
	}
	public async setObject(val:object) {
		this._object = val;
		this.clearRows();
		const rows = ObjectTreeCtrl.objectToTree(val);
		await this.appendRows(rows, undefined, (rows.length === 1 || (this.options as ObjectTreeCtrlOptions).expandRoot) ? 1 : 0);
	}
	public get object(): object {
		return this._object;
	}

	static isObj(x: any): boolean {
		return x && typeof x === 'object' && !ObjectTreeCtrl.objectToTreeValueTypes.some(t => x instanceof t);
	}
	static objectToTree(obj: any): PromisedTreeNode[] {
		return Object.entries(obj).map(([key,value]) => ObjectTreeCtrl.node(key, value, obj));
	}
	static node(name: string, _value: any, obj: any): PromisedTreeNode {
		const _children = [];
		const _lazyLoadChildren = ObjectTreeCtrl.isObj(_value);
		const value = ObjectTreeCtrl.isObj(_value) ? '' : _value;
		return [
			{ name, value, [ObjectTreeCtrl.kvObjectRef]: obj, type: (typeof obj[name] === 'object' && obj[name] && Object.getPrototypeOf(obj[name])?.constructor?.name) || typeof obj[name], _lazyLoadChildren },
			_children
		];
	}


	async _buildContextMenu(targetItems : any[], o: any, cell: HTMLDivElement, e: MouseEvent) {
		const actions = await super._buildContextMenu(targetItems, o, cell, e);
		if (this.options.editable && targetItems.length === 1) {
			actions.push({ text: '-' })
			const parent = o[ObjectTreeCtrl.kvObjectRef];
			const target = parent[o.name];
			if (target instanceof Array) {
				actions.push({
					text: 'Add item', action: () => {
						const newItem = target.length > 0 ? structuredClone(target[target.length - 1]) : "", newIndex = target.length;
						target.push(newItem);
						this.appendRows([ObjectTreeCtrl.node(""+newIndex, newItem, target)], o);
					},
				});
			}
			if (parent instanceof Array) {
				actions.push({
					text: 'Duplicate', action: () => {
						const newItem = structuredClone(target), newIndex = +o.name;
						parent.splice(newIndex+1, 0, newItem);
						this.appendRows([ObjectTreeCtrl.node(""+newIndex, newItem, parent)], this.getParentOf(o));
					},
				})
			}
			/*if (target !== null)
				actions.push({
					text: 'Set to null', action: () => {
						parent[o.name] = null;
					},
				});*/
			actions.push({
				text: 'Remove', action: () => {
					delete parent[o.name];
					o[GridCtrl._tr].remove();
					this.data.splice(this.data.indexOf(o), 1);
				},
			});
		}
		actions.push({ text: '-' })
		if (cell?.getAttribute('data-col-key') === 'name') {
			actions.push({
				text: 'Copy JSON Path', action: () => {
					const path = this.getJsonPathOf(o);
					navigator.clipboard.writeText(path.join("."));
					UiContext.instance.toast("Copied "+path.join("."), 2500, [['with types', () => navigator.clipboard.writeText(this.getJsonPathOf(o, true).join("."))]])
				},
			})
		}
		actions.push({
			text: 'Copy Contents as JSON', action: () => navigator.clipboard.writeText(JSON.stringify(targetItems.map(o => ({ [o.name]: o[ObjectTreeCtrl.kvObjectRef][o.name] })))),
		});
		actions.push({
			text: 'Refresh', action: () => this.setObject(this._object),
		});
		return actions;
	}

	getJsonPathOf(o, withTypes: boolean = false) {
		let node = o;
		const path = [];
		const typedpath = [];
		while (node) {
			if (withTypes)
				path.unshift(node.name + '(' + node.type + ')')
			else
				path.unshift(node.name);
			node = this.getParentOf(node);
		}
		const skip = (this.options as ObjectTreeCtrlOptions).pathSkipLevels || 0;
		path.splice(0, skip);
		return path;
	}

	async fillRowWithData(tr: HTMLDivElement, o: any, forceRerender: boolean): Promise<RowRenderResult> {
		const r = super.fillRowWithData(tr, o, forceRerender);
		const firstCol = tr.firstElementChild as HTMLElement;
		firstCol.draggable = true;
		firstCol.ondragstart = async (event) => {
		  event.dataTransfer.effectAllowed = "link";
		  event.dataTransfer.setData("text/vnd.pinboard-json-path+plain", this.getJsonPathOf(o).join("."));
		}
		return r;
	}

	/**
	 * Displays a generic dialog pane with an ObjectTreeCtrl rendering the contents of `obj`.
	 *
	 * @param obj the object to display
	 * @param message passed as `message` to SearchableMenu (smaller font headline - for large font headline, add `title` in dialogOptions)
	 * @param treeOptions passed to ObjectTreeCtrl constructor
	 * @param dialogOptions passed to `SearchableMenu.show`, extended with the ObjectTreeCtrl as customElement, center: false, width: 500, and buttons extended with a
	 * 						'↗️' button to show contents in a new window
	 * @see SearchableMenu.show
	 */
	static async showDialog(obj: any, message?: string, treeOptions?: ObjectTreeCtrlOptions, dialogOptions?: SearchableMenuOptions) {
		const root = EL('div', { style: {'user-select':'text'} });
		const tree = new ObjectTreeCtrl(root, Object.assign({ data: obj, initialCols: [{key:'name',edit:'',align:'left'},{key:'value',width:300,align:'left'}], settingsKey: 'objectTree' }, treeOptions));
		if ((obj instanceof Array && obj.length < 5) || (tree.data.length === 1)) {
			for(let i = tree.data.length - 1; i >= 0; i--) tree.toggleExpand(tree.data[i]);
		}
		const buttons = dialogOptions?.buttons || [];
		const result = await ShowDialog(message, [], Object.assign({
			customElement: Promise.resolve(root), center: false, width: 500,
		}, dialogOptions, { buttons: buttons.concat(['↗️']), }));
		if (result === '↗️') {
			const wnd = new DomWindow();
			wnd.props = obj;
			wnd.setTitle(`${message || wnd.id} - ObjectTreeCtrl`);
			wnd.contentEl.append(root.children[0]);
			return new Promise((resolve, reject) => {
				for(const button of buttons) wnd.addTitleButton(button, () => resolve(button));
				wnd[Symbol.dispose].defer(() => resolve({}));
			});
		}
		return result;
	}

	afterCellEdit(col : ColInfo, o : any, newValue : any) {
		if (col.key === 'value' && ObjectTreeCtrl.kvObjectRef in o) o[ObjectTreeCtrl.kvObjectRef][o.name] = newValue;
		col.assign(o, newValue);
		this.afterCellEdited.emit(col, o, newValue);
	}

	onBeforeExpand(treeRow : HTMLDivElement, o : any) {
		if (!o['_lazyLoadChildren']) return;
        try {
    		o['_lazyLoadChildren'] = false;
			const rows = ObjectTreeCtrl.objectToTree(o[ObjectTreeCtrl.kvObjectRef][o.name]);
    		this.appendRows(rows, o, rows.length === 1 ? 1 : 0);
        } catch {
            o['_lazyLoadChildren'] = true;
        }
	}

	getParentOf(o : any) : any {
		return this.dataFromElement(o[GridCtrl._tr].parentElement.parentElement.parentElement.firstChild);
	}
}

export class SchemaTreeCtrl extends TreeCtrl {
	public static readonly kvObjectRef = Symbol('kvObjectRef');
	public static readonly objectToTreeValueTypes = [Uint8Array, ArrayBuffer];

	static isObj(x: any): boolean {
		return x && typeof x === 'object' && !ObjectTreeCtrl.objectToTreeValueTypes.some(t => x instanceof t);
	}
	static objectToTree(obj: any): PromisedTreeNode[] {
		return Object.entries(obj).map(([key,value]) => SchemaTreeCtrl.node(key, ObjectTreeCtrl.isObj(value) ? '' : value, [] , obj, ObjectTreeCtrl.isObj(value)));
	}
	static node(name: string, value: any, children: PromisedTreeNode[] | Promise<PromisedTreeNode[]>, obj: any, _lazyLoadChildren = false): PromisedTreeNode {
		if (children === undefined) children = [];
		if (!(children instanceof Array || children instanceof Promise)) throw "invalid children type"+children;
		return [ { name, value, [ObjectTreeCtrl.kvObjectRef]: obj, type: (typeof obj[name] === 'object' && obj[name] && Object.getPrototypeOf(obj[name])?.constructor?.name) || typeof obj[name], _lazyLoadChildren }, children ];
	}

	static async showDialog(obj: any, message?: string, treeOptions?: GridCtrlOverrideOptions, dialogOptions?: SearchableMenuOptions) {
		const root = EL('div', { style: {'user-select':'text'} });
		const tree = new ObjectTreeCtrl(root, Object.assign({ data: ObjectTreeCtrl.objectToTree(obj), initialCols: [{key:'name',edit:'',align:'left'},{key:'value',width:300,align:'left'}], settingsKey: 'objectTree' }, treeOptions));
		return ShowDialog(message, [], Object.assign({
			customElement: Promise.resolve(root), center: false, width: 500,
		}, dialogOptions));
	}

	afterCellEdit(col : ColInfo, o : any, newValue : any) {
		if (col.expression) return;
		if (ObjectTreeCtrl.kvObjectRef in o) o[ObjectTreeCtrl.kvObjectRef][o.name] = newValue;
		o[col.key] = newValue;
		this.afterCellEdited.emit(col, o, newValue);
	}

	onBeforeExpand(treeRow : HTMLDivElement, o : any) {
		if (!o['_lazyLoadChildren']) return;
        try {
    		o['_lazyLoadChildren'] = false;
    		this.appendRows(ObjectTreeCtrl.objectToTree(o[ObjectTreeCtrl.kvObjectRef][o.name]), o);
        } catch {
            o['_lazyLoadChildren'] = true;
        }
	}

}

if (window.customElements) {
	window.customElements.define('grid-ctrl', class extends HTMLElement {
		public grid: GridCtrl;
		private _shadowRoot: ShadowRoot;

		constructor() {
			super();
			this._shadowRoot = this.attachShadow({mode: "open"});
		}

		connectedCallback() {
			this._shadowRoot.replaceChildren(EL('style', {}, GridCtrlStyles));
			this.grid = new GridCtrl(this._shadowRoot, JSON.parse(this.getAttribute('data-options')));
		}
	});
	window.customElements.define('tree-ctrl', class extends HTMLElement {
		public grid: TreeCtrl;
		public object: object;
		public options: GridCtrlOverrideOptions;
		private _shadowRoot: ShadowRoot;

		constructor() {
			super();
			this._shadowRoot = this.attachShadow({mode: "open"});
		}

		connectedCallback() {
			this._shadowRoot.replaceChildren(EL('style', {}, GridCtrlStyles));
			let options = this.options || JSON.parse(this.getAttribute('data-options'));
			if (this.object) {
				options = Object.assign({
					data: this.object,
					initialCols: [{key: 'name', edit: '', align: 'left'}, {key: 'value', width: 300, align: 'left'}],
					settingsKey: 'objectTree'
				}, options);
				this.grid = new ObjectTreeCtrl(this._shadowRoot, options);
			} else if (this.hasAttribute('data-object')) {
				options = Object.assign({
					data: JSON.parse(this.getAttribute('data-object')),
					initialCols: [{key: 'name', edit: '', align: 'left'}, {key: 'value', width: 300, align: 'left'}],
					settingsKey: 'objectTree'
				}, options);
				this.grid = new ObjectTreeCtrl(this._shadowRoot, options);
			} else {
				this.grid = new TreeCtrl(this._shadowRoot, options);
			}
		}
	});
}

document.addEventListener('DOMContentLoaded', function() {
	document.head.appendChild(EL('style', {}, GridCtrlStyles));
});
